import React, { useState } from 'react';
import { Button } from '@mui/material';
import './Login.css';
import Particles from '../UI/blocks/Backgrounds/Particles/Particles';
import { Visibility, VisibilityOff, Email as EmailIcon, Lock, Message } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { useAuthAttemptMutation, useLoginUserMutation, useVerifyUserMutation } from '../store/services/user/user';
import { useDispatch } from 'react-redux';
import { setJwt } from '../store/slices/authSlice';
import CustomGreenButton from '../UI/Intro/customButton/customGreenButton';

const Login = () => {
    const dispatch = useDispatch();
    const [userInput, setUserInput] = useState({
        email: '',
        password: '',
        otp: ''
    });
    const [loginMessage, setLoginMessage] = useState({
        email: '',
        password: '',
        otp: '',
        general: ''
    });
    const [loading, setIsLoading] = useState(false);
    const [authStep, setAuthStep] = useState('email'); // 'email', 'password', or 'otp'
    const [showPassword, setShowPassword] = useState(false);
    const cloudImg = 'https://s3.ap-south-1.amazonaws.com/email.machinehack.assets/Illustration.png';

    // RTK Query hooks
    const [authAttempt] = useAuthAttemptMutation();
    const [loginUser] = useLoginUserMutation();
    const [verifyUser] = useVerifyUserMutation();

    const handleEmailSubmit = async () => {
        setLoginMessage({ email: '', password: '', otp: '', general: '' });
        let hasErrors = false;
        const errors = { email: '', password: '', otp: '', general: '' };

        if (!userInput.email) {
            errors.email = 'Email is required';
            hasErrors = true;
        } else if (!/\S+@\S+\.\S+/.test(userInput.email)) {
            errors.email = 'Invalid email address';
            hasErrors = true;
        }

        if (hasErrors) {
            setLoginMessage(errors);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const response = await authAttempt({
                email: userInput.email,
                platform: 'dev.datalyze.machinehack.com'
            }).unwrap();

            if (response.data.passwordEnabled) {
                setAuthStep('password');
            } else {
                setAuthStep('otp');
            }
        } catch (error) {
            console.error('Auth attempt error:', error);
            setLoginMessage({
                ...errors,
                general: error.data?.message || error.message || 'An unexpected error occurred'
            });
        }
        setIsLoading(false);
    };

    const handlePasswordSubmit = async () => {
        setLoginMessage({ email: '', password: '', otp: '', general: '' });
        let hasErrors = false;
        const errors = { email: '', password: '', otp: '', general: '' };

        if (!userInput.password) {
            errors.password = 'Password is required';
            hasErrors = true;
        }

        if (hasErrors) {
            setLoginMessage(errors);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const response = await loginUser({
                email: userInput.email,
                password: userInput.password,
                platform: 'dev.datalyze.machinehack.com'
            }).unwrap();

            if (response.data?.token) {
                dispatch(setJwt({ token: response.data.token }));
                setShowLogin(false);
            } else {
                setLoginMessage({ ...errors, general: 'Login failed' });
            }
        } catch (error) {
            console.error('Login error:', error);
            setLoginMessage({
                ...errors,
                general: error.data?.message || 'An unexpected error occurred. Please try again.'
            });
        }
        setIsLoading(false);
    };

    const handleOtpSubmit = async () => {
        setLoginMessage({ email: '', password: '', otp: '', general: '' });
        let hasErrors = false;
        const errors = { email: '', password: '', otp: '', general: '' };

        if (!userInput.otp) {
            errors.otp = 'OTP is required';
            hasErrors = true;
        }

        if (hasErrors) {
            setLoginMessage(errors);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const response = await verifyUser({
                email: userInput.email,
                otp: userInput.otp,
                platform: 'dev.datalyze.machinehack.com'
            }).unwrap();

            if (response.data?.token) {
                dispatch(setJwt({ token: response.data.token }));
                setShowLogin(false);
            } else {
                setLoginMessage({ ...errors, general: 'Verification failed' });
            }
        } catch (error) {
            console.error('Login error:', error);
            setLoginMessage({
                ...errors,
                general: error.data?.message || 'An unexpected error occurred. Please try again.'
            });
        }
        setIsLoading(false);
    };

    const handleChangeEmail = () => {
        setAuthStep('email');
        setUserInput({ ...userInput, otp: '' });
        setLoginMessage({ email: '', password: '', otp: '', general: '' });
    };

    const handleChange = (e, field) => {
        const value = e.target.value;
        setUserInput({ ...userInput, [field]: value });
    };

    const handleOtpChange = (e) => {
        const value = e.target.value;
        // Ensure OTP is treated as a string and only contains numbers
        const otpValue = value.replace(/[^0-9]/g, '');
        setUserInput({ ...userInput, otp: otpValue });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const getStepHeading = () => {
        switch (authStep) {
            case 'email':
                return 'Welcome';
            case 'password':
                return 'Enter your password';
            case 'otp':
                return 'Enter verification code';
            default:
                return 'Welcome';
        }
    };

    // Updated getStepDescription to include email
    const getStepDescription = () => {
        switch (authStep) {
            case 'email':
                return 'Please enter your email to continue';
            case 'password':
                return `Please enter your password for ${userInput.email}`;
            case 'otp':
                return `We've sent a verification code to ${userInput.email}`;
            default:
                return '';
        }
    };

    // Updated renderAuthInput with email display and change email option
    const renderAuthInput = () => {
        switch (authStep) {
            case 'email':
                return (
                    <>
                        <div className="input-wrapper">
                            <div className="input-icon">
                                <EmailIcon width={20} height={20} />
                            </div>
                            <input
                                type="text"
                                placeholder="Enter your email"
                                onChange={(e) => handleChange(e, 'email')}
                                className="input-field"
                                value={userInput.email || ''}
                            />
                        </div>
                        {loginMessage.email && (
                            <div className="error-message">
                                <span>{loginMessage.email}</span>
                            </div>
                        )}
                    </>
                );
            case 'password':
                return (
                    <>
                        <div className="input-wrapper">
                            <div className="input-icon">
                                <Lock width={20} height={20} />
                            </div>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your password"
                                onChange={(e) => handleChange(e, 'password')}
                                className="input-field password-input" // Add this class
                                value={userInput.password || ''}
                            />
                            {userInput.password && (
                                <IconButton
                                    className="password-toggle"
                                    onClick={togglePasswordVisibility}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            )}
                        </div>
                        <Button
                            className="change-email-btn"
                            onClick={handleChangeEmail}
                            type="button"
                            sx={{
                                textTransform: 'none',
                            }}
                        >
                            Change email?
                        </Button>
                        {loginMessage.password && (
                            <div className="error-message">
                                <span>{loginMessage.password}</span>
                            </div>
                        )}
                    </>
                );
            case 'otp':
                return (
                    <>
                        <div className="input-wrapper">
                            <div className="input-icon">
                                <Message />
                            </div>
                            <input
                                type="text"
                                placeholder="Enter verification code"
                                onChange={handleOtpChange}
                                className="input-field"
                                value={userInput.otp || ''}
                            />
                        </div>
                        <Button
                            className=""
                            onClick={handleChangeEmail}
                            type="button"
                            sx={{
                                backgroundColor: 'transparent',
                                textTransform: 'none',
                                textDecoration: 'none',
                                '&hover': {
                                    textDecoration: 'underline',
                                }
                            }}
                        >Change email?</Button>
                        {loginMessage.otp && (
                            <div className="error-message">
                                <span>{loginMessage.otp}</span>
                            </div>
                        )}
                    </>
                );
            default:
                return null;
        }
    };

    const getButtonText = () => {
        switch (authStep) {
            case 'email':
                return 'Continue';
            case 'password':
                return 'Sign In';
            case 'otp':
                return 'Verify';
            default:
                return 'Continue';
        }
    };

    const handleSubmit = () => {
        switch (authStep) {
            case 'email':
                handleEmailSubmit();
                break;
            case 'password':
                handlePasswordSubmit();
                break;
            case 'otp':
                handleOtpSubmit();
                break;
            default:
                handleEmailSubmit();
        }
    };



    return (
        <div className="login-container">
            <div className="login-content">
                <div className="login-form-section">
                    <div className="login-card">
                        <h1 className="login-title">CloudLab</h1>
                        <div className="auth-step-info">
                            <h2 className="step-heading">{getStepHeading()}</h2>
                            <p className="step-description">{getStepDescription()}</p>
                        </div>
                        <form
                            onSubmit={e => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            className="input-group"
                        >
                            {renderAuthInput()}
                            {loginMessage.general && (
                                <div className="error-message general-error">
                                    <span>{loginMessage.general}</span>
                                </div>
                            )}
                            <CustomGreenButton
                                width="100%"
                                firstText={getButtonText()}
                                secondText={getButtonText()}
                                handleClick={handleSubmit}
                                type="submit"
                                disabled={loading}
                                loading={loading}
                            />
                        </form>
                    </div>
                </div>
                <div className="image-section">
                    <div className="image-container">
                        <img src={cloudImg} alt="cloud-lab" className="feature-image" />
                    </div>
                </div>
            </div>
            <Particles
                className="particles-background"
                particleColors={['#00FF85', '#00E5FF']}
                particleCount={200}
                particleSpread={10}
                speed={0.2}
                particleBaseSize={300}
                moveParticlesOnHover={true}
                alphaParticles={true}
                disableRotation={false}
            />
        </div>
    );
};

export default Login;   